import type { CefrLevel, Language, WordState } from "@rmnlng/shared";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { apiFetch } from "./api";
import { useMe } from "./auth";
import { features } from "./features";

// See `WORDS_PAGE_SIZE` in words.ts — same rationale.
const GRAMMAR_PAGE_SIZE = 50;

// Sort variants supported by the API. `default` = updatedAt desc (the
// classic flat-list view). `suggested` = topo-sort by prerequisiteSlugs
// — used in LEVEL mode to drive the "Up next" hero.
export type GrammarOrder = "default" | "suggested";

export type GrammarListItem = {
  id: string;
  title: string;
  summary: string | null;
  tags: string[];
  updatedAt: string;
  state: WordState;
};

export type GrammarListTotals = {
  total: number;
  known: number;
  learning: number;
  new: number;
  // CEFR levels that actually have rules in the corpus. Drives the
  // chip row on /grammar — we only render chips for levels with
  // content so empty buckets don't show up. Stable across the active
  // filters (mirrors `words.totals.levels`).
  levels: CefrLevel[];
};

export type GrammarList = {
  items: GrammarListItem[];
  total: number;
  limit: number;
  offset: number;
  totals: GrammarListTotals;
};

export type GrammarProgressAgg = {
  state: "learning" | "known";
  easeFactor: number;
  repetitions: number;
  intervalDays: number;
  nextReviewAt: string;
  lastReviewedAt: string | null;
};

// Lightweight shape for rules cross-linked from another rule. Matches what
// `GrammarService.resolveRelatedRules` returns.
export type RelatedGrammarRule = {
  id: string;
  title: string;
  slug: string | null;
  summary: string | null;
};

export type GrammarDetail = {
  id: string;
  title: string;
  slug: string | null;
  summary: string | null;
  body: string;
  createdAt: string;
  updatedAt: string;
  tags: string[];
  progress: GrammarProgressAgg | null;
  // Union of outgoing + inbound cross-links, resolved server-side from
  // `relatedRuleSlugs` on this rule and every other rule. Deduped, sorted
  // by title.
  relatedRules: RelatedGrammarRule[];
  // Number of authored quiz items for this rule. Drives the GrammarDetail
  // "Quiz yourself" footer card (Phase 5a) — hidden when 0.
  quizItemCount: number;
  // Inline MCQ examples shown right after the rule body in the
  // Scenarios session's RULE step. Empty / missing = text-only rule.
  examples: GrammarExample[];
};

export type GrammarExample = {
  prompt: string;
  options: string[];
  answer: string;
};

// Full set of params that affect the grammar list query. `level` and
// `order` are auto-filled by `useGrammarList` based on the user's mode;
// callers can override either by passing explicit values.
export type GrammarListQuery = {
  q?: string;
  state?: WordState;
  tag?: string;
  level?: CefrLevel;
  order?: GrammarOrder;
};

export const grammarKeys = {
  all: ["grammar"] as const,
  list: (query: GrammarListQuery) => ["grammar", "list", query] as const,
  detail: (id: string) => ["grammar", "detail", id] as const,
};

/**
 * Grammar list, infinite-paginated. In Level mode the hook auto-fills
 * `level=user.currentLevel` + `order=suggested` so the page renders the
 * pedagogical order without each call site re-deriving it from `useMe`.
 *
 * Open mode sticks to the existing flat list (updatedAt desc) — overrides
 * are passed-through verbatim so callers needing a specific scope (e.g.
 * `LevelFocusPage`'s "browse A2 grammar" link via `?tag=A2`) still work.
 */
export function useGrammarList(query: GrammarListQuery = {}) {
  const { data: user } = useMe();
  const isLevel = user?.studyMode === "LEVEL";

  // Auto-fill level + order in Level mode unless caller already set them.
  const resolved: GrammarListQuery = {
    ...query,
    level: query.level ?? (isLevel ? user?.currentLevel : undefined),
    order: query.order ?? (isLevel ? "suggested" : "default"),
  };

  return useInfiniteQuery({
    queryKey: grammarKeys.list(resolved),
    initialPageParam: 0,
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams();
      if (resolved.q) params.set("q", resolved.q);
      if (resolved.state) params.set("state", resolved.state);
      if (resolved.tag) params.set("tag", resolved.tag);
      if (resolved.level) params.set("level", resolved.level);
      if (resolved.order && resolved.order !== "default") params.set("order", resolved.order);
      params.set("limit", String(GRAMMAR_PAGE_SIZE));
      params.set("offset", String(pageParam));
      return apiFetch<GrammarList>(`/grammar?${params.toString()}`);
    },
    // Same pattern as useWordsList — offset-based pagination, `undefined`
    // means "done".
    getNextPageParam: (last) => {
      const loaded = last.offset + last.items.length;
      return loaded < last.total ? loaded : undefined;
    },
    placeholderData: (prev) => prev,
    // Wait for `useMe` to resolve before firing — otherwise the first
    // request goes out without level/order, then refetches once the user
    // data lands. Same pattern as `useReviewQueue`.
    enabled: features.grammarSection && !!user,
  });
}

export function useGrammarRule(id: string | undefined) {
  return useQuery({
    queryKey: grammarKeys.detail(id ?? ""),
    queryFn: () => apiFetch<GrammarDetail>(`/grammar/${id}`),
    enabled: !!id,
  });
}

// Words that a given grammar rule applies to. Returned by
// `GET /grammar/:id/words` and rendered under "Applies to" on
// GrammarDetail.
export type GrammarRuleWord = {
  id: string;
  lemma: string;
  language: Language;
  pos: string | null;
  gender: string | null;
  matchedBy: "matchers" | "lemma" | "tags";
};

// Server returns a capped preview plus the full match count, so the UI can
// show "X total" while rendering only the first 10 chips.
export type GrammarRuleWords = {
  total: number;
  items: GrammarRuleWord[];
};

export function useGrammarRuleWords(id: string | undefined) {
  return useQuery({
    queryKey: ["grammar", "words", id ?? ""],
    queryFn: () => apiFetch<GrammarRuleWords>(`/grammar/${id}/words`),
    enabled: !!id,
  });
}

/** First char of each of the first two words, uppercased; or first two of a
 *  single word. Matches the design's icon-chip convention.
 */
export function iconChip(title: string): string {
  const words = title.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  if (words.length === 1) {
    const w = words[0]!;
    return w.slice(0, Math.min(2, w.length)).toLowerCase();
  }
  return (words[0]!.charAt(0) + words[1]!.charAt(0)).toLowerCase();
}
